import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { palette } from "@/styles/palette";
import StarRating from "./StarRating";
import FlavorSelector from "./FlavorSelector";
import AromaSelector from "./AromaSelector";
import CommonButton from "@/components/common/CommonButton";
import { AromaCategory, ReviewData } from "@/types";
import { fontFamily } from "@/styles/font";

// 페이드 인 애니메이션 정의
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

interface ReviewFormProps {
    productAltKey: string;
    productName: string;
    isReadOnly?: boolean;
    initialData?: {
        rating: number;
        tasteValues: {
            sweetness: number;
            sourness: number;
            freshness: number;
            thickness: number;
        };
        reviewText: string;
        selectedTags: number[];
        uploadedImage?: string;
    };
    aromaTags: AromaCategory[];
    onSubmit: (reviewData: ReviewData) => void;
    onCancel?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
    productAltKey,
    productName,
    isReadOnly = false,
    initialData,
    aromaTags,
    onSubmit,
    onCancel
}) => {
    // 별점
    const [rating, setRating] = useState(initialData?.rating || 0);

    // 맛 평가 값
    const [tasteValues, setTasteValues] = useState(
        initialData?.tasteValues || {
            sweetness: 0,
            sourness: 0,
            freshness: 0,
            thickness: 0
        }
    );

    // 리뷰 텍스트
    const [reviewText, setReviewText] = useState(initialData?.reviewText || "");

    // 선택된 태그
    const [selectedTags, setSelectedTags] = useState<number[]>(initialData?.selectedTags || []);

    // 업로드된 이미지
    const [uploadedImage, setUploadedImage] = useState<string | null>(initialData?.uploadedImage || null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 섹션 표시 상태
    const [showTasteSection, setShowTasteSection] = useState(isReadOnly || !!initialData || false);
    const [showReviewSection, setShowReviewSection] = useState(isReadOnly || !!initialData || false);
    const [showPhotoTagSection, setShowPhotoTagSection] = useState(isReadOnly || !!initialData || false);

    // 포커스 상태 추적
    const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);

    // 각 섹션에 대한 ref 생성
    const tasteSectionRef = useRef<HTMLDivElement>(null);
    const reviewSectionRef = useRef<HTMLDivElement>(null);
    const photoTagSectionRef = useRef<HTMLDivElement>(null);

    // 안전한 스크롤 함수
    const safeScrollIntoView = (ref: React.RefObject<HTMLDivElement>, delay = 100) => {
        // 텍스트 영역에 포커스가 있으면 스크롤하지 않음
        if (isTextAreaFocused) return;

        setTimeout(() => {
            if (ref.current) {
                ref.current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest" // 가능한 적은 스크롤을 발생시킴
                });
            }
        }, delay);
    };

    const handleTasteChange = (taste: keyof typeof tasteValues) => (value: number) => {
        if (isReadOnly) return;

        setTasteValues((prev) => {
            const newValues = { ...prev, [taste]: value };
            if (Object.values(newValues).some((v) => v > 0) && !showReviewSection) {
                setShowReviewSection(true);
                // 하나라도 값이 변경되었고, 다른 맛도 이미 설정된 경우에만 스크롤
                const hasOtherTastes = Object.entries(newValues)
                    .filter(([key]) => key !== taste)
                    .some(([_, value]) => value > 0);

                // 현재 변경한 것이 첫 번째 맛 선택이 아니거나 사용자가 의도적으로 값을 변경한 경우 스크롤
                if (hasOtherTastes || value > 0) {
                    safeScrollIntoView(reviewSectionRef, 300);
                }
            }
            return newValues;
        });
    };

    const handleReviewChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (isReadOnly) return;

        setReviewText(e.target.value);
        if (e.target.value.length >= 10 && !showPhotoTagSection) {
            setShowPhotoTagSection(true);
            // 텍스트 입력 시에는 자동 스크롤하지 않음
        }
    };

    const handleTagClick = (aromaId: number) => {
        if (isReadOnly) return;

        setSelectedTags((prevSelectedTags) => {
            if (prevSelectedTags.includes(aromaId)) {
                return prevSelectedTags.filter((id) => id !== aromaId);
            } else {
                // 최대 5개까지만 선택 가능
                if (prevSelectedTags.length >= 5) {
                    return prevSelectedTags;
                }
                return [...prevSelectedTags, aromaId];
            }
        });
    };

    const handleImageUpload = () => {
        if (isReadOnly) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isReadOnly) return;

        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                // 5MB 제한
                alert("이미지 크기는 5MB를 초과할 수 없습니다.");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageDelete = () => {
        if (isReadOnly) return;

        setUploadedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const isReviewValid = reviewText.length === 0 || reviewText.length >= 10;

    const isSubmitEnabled = () => {
        if (isReadOnly) return true;

        const hasTasteRating = Object.values(tasteValues).some((value) => value > 0);
        const hasValidReview = reviewText.length >= 10;
        return rating > 0 && hasTasteRating && hasValidReview;
    };

    const handleSubmit = () => {
        if (isReadOnly) return;

        const reviewData: ReviewData = {
            productAltKey,
            customProductAltKey: null,
            content: reviewText,
            rating,
            sweetness: tasteValues.sweetness,
            sourness: tasteValues.sourness,
            carbonation: tasteValues.thickness,
            aromaStrength: tasteValues.freshness,
            aromaIdList: selectedTags.map((tag) => parseInt(tag.toString())),
            reviewDate: new Date().toISOString().split("T")[0],
            images: fileInputRef.current?.files ? Array.from(fileInputRef.current.files) : []
        };

        onSubmit(reviewData);
    };

    return (
        <Container>
            <Content>
                {/* 별점 평가 섹션 */}
                <Section>
                    <SectionTitle>{productName} 어떠셨나요?</SectionTitle>
                    <SectionContent>
                        <StarRating
                            rating={rating}
                            onRatingChange={(value) => {
                                if (isReadOnly) return;
                                setRating(value);
                                setShowTasteSection(true); // 별점 선택 시 다음 섹션 표시
                                safeScrollIntoView(tasteSectionRef, 300);
                            }}
                        />
                    </SectionContent>
                </Section>

                {/* 맛 평가 섹션 */}
                {showTasteSection && (
                    <Section ref={tasteSectionRef}>
                        <SectionTitle>맛은 어땠나요?</SectionTitle>
                        <SectionContent>
                            <FlavorSelector
                                tasteValues={tasteValues}
                                onChange={handleTasteChange}
                                readOnly={isReadOnly}
                            />
                        </SectionContent>
                    </Section>
                )}

                {/* 상세 리뷰 섹션 */}
                {showReviewSection && (
                    <Section ref={reviewSectionRef}>
                        <SectionTitle>자세한 이야기를 들려주세요</SectionTitle>
                        <SectionContent>
                            <TextAreaWrapper>
                                <ReviewTextArea
                                    placeholder="술과 함께한 경험을 자세히 들려주세요 (최소 10자)"
                                    maxLength={500}
                                    value={reviewText}
                                    onChange={handleReviewChange}
                                    $isValid={isReviewValid}
                                    readOnly={isReadOnly}
                                    onFocus={() => setIsTextAreaFocused(true)}
                                    onBlur={() => {
                                        setIsTextAreaFocused(false);
                                        // 텍스트 입력 완료 후 다음 섹션 표시 조건 충족하면 스크롤
                                        if (reviewText.length >= 10 && !isReadOnly) {
                                            safeScrollIntoView(photoTagSectionRef, 300);
                                        }
                                    }}
                                />
                                <TextCounter $isValid={isReviewValid}>{reviewText.length}/500</TextCounter>
                                {!isReadOnly && <DragHandle />}
                            </TextAreaWrapper>
                        </SectionContent>
                    </Section>
                )}

                {/* 사진 업로드 및 태그 선택 섹션 */}
                {showPhotoTagSection && (
                    <Section ref={photoTagSectionRef}>
                        <SectionTitle>
                            사진으로 남겨보세요
                            <SubText>선택</SubText>
                        </SectionTitle>
                        <SectionContent>
                            <PhotoUploadArea>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    disabled={isReadOnly}
                                />
                                {uploadedImage ? (
                                    <UploadedImageContainer>
                                        <UploadedImage src={uploadedImage} alt="업로드된 이미지" />
                                        {!isReadOnly && (
                                            <DeleteButton onClick={handleImageDelete}>
                                                <img src="/icon/close.png" alt="삭제" />
                                            </DeleteButton>
                                        )}
                                    </UploadedImageContainer>
                                ) : (
                                    <PhotoUploadButton onClick={handleImageUpload} disabled={isReadOnly}>
                                        <img src="/icon/camera.png" alt="사진 업로드" />
                                        <span>{uploadedImage ? "1/1" : "0/1"}</span>
                                    </PhotoUploadButton>
                                )}
                            </PhotoUploadArea>
                        </SectionContent>
                        <SectionContent> </SectionContent>
                        <SectionTitle>
                            향과 맛을 선택해주세요
                            <SubText>선택</SubText>
                        </SectionTitle>
                        <SectionDescription>
                            선택한 취향을 바탕으로 맞춤 추천이 제공됩니다 (최대 5개)
                        </SectionDescription>
                        <SectionContent>
                            <AromaSelector
                                aromaTags={aromaTags}
                                selectedTags={selectedTags}
                                onTagClick={handleTagClick}
                                readOnly={isReadOnly}
                            />
                        </SectionContent>
                    </Section>
                )}
            </Content>

            {!isReadOnly ? (
                <ButtonWrapper>
                    <CommonButton onClick={handleSubmit} enabled={isSubmitEnabled()} text="완료" />
                </ButtonWrapper>
            ) : (
                onCancel && (
                    <ButtonWrapper>
                        <CommonButton onClick={onCancel} enabled={true} text="뒤로가기" />
                    </ButtonWrapper>
                )
            )}
        </Container>
    );
};

export default ReviewForm;

const Container = styled.div`
    width: 100%;
    background-color: ${palette.white};
    padding-bottom: calc(58px + 32px);
    position: relative;
    overflow-x: hidden; // 혹시 모를 가로 스크롤 방지
`;

const Content = styled.main`
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Section = styled.section`
    box-sizing: border-box;
    width: 100%;
    max-width: 361px;
    background: #ffffff;
    box-shadow:
        0px 16px 32px -4px rgba(12, 12, 13, 0.1),
        0px 4px 4px -4px rgba(12, 12, 13, 0.05);
    border-radius: 20px;
    padding: 27px 28px;
    margin: 0 auto 32px;
    animation: ${fadeIn} 0.5s ease-in-out; // 애니메이션 적용
`;

const SectionContent = styled.div`
    margin-top: 24px;
    width: 100%;

    &:first-of-type {
        margin-top: 16px;
    }
`;

const SectionTitle = styled.h2`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 9px;
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: #000000;
    margin: 0;
`;

const SectionDescription = styled.p`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 14px;
    line-height: 17px;
    color: #9d9d9d;
    margin: 8px 0 0 0;
    width: 100%;
`;

const SubText = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 14px;
    color: #5f5f5f;
    display: flex;
    align-items: center;
`;

const TextAreaWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 138px;
`;

const ReviewTextArea = styled.textarea<{ $isValid: boolean }>`
    width: 100%;
    height: 138px;
    background: #ffffff;
    border: 1px solid ${(props) => (props.$isValid ? palette.gray[300] : palette.red)};
    box-sizing: border-box;
    border-radius: 8px;
    font-family: ${fontFamily.base};
    font-size: 16px;
    line-height: 1.5;
    padding: 12px;
    resize: none;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: ${palette.primary};
    }

    &::placeholder {
        color: ${palette.gray[400]};
    }
`;

const TextCounter = styled.span<{ $isValid: boolean }>`
    position: absolute;
    right: 16px;
    bottom: 12px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 14px;
    color: ${(props) => (props.$isValid ? "#414141" : "#FF4D4F")};
`;

const DragHandle = styled.div`
    position: absolute;
    width: 6.63px;
    height: 6.63px;
    right: 5.02px;
    bottom: 6.02px;
    border: 1px solid #b3b3b3;
    pointer-events: none;
`;

const PhotoUploadArea = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 10px;
    width: 84px;
    height: 84px;
`;

const PhotoUploadButton = styled.button`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 19px 26px;
    gap: 4px;
    width: 84px;
    height: 84px;
    background: #ffffff;
    border: 1px dashed #d9d9d9;
    border-radius: 10px;
    cursor: ${(props) => (props.disabled ? "default" : "pointer")};
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};

    img {
        width: 32px;
        height: 32px;
    }

    span {
        font-family: ${fontFamily.base};
        font-style: normal;
        font-weight: 350;
        font-size: 12px;
        line-height: 14px;
        color: #9d9d9d;
    }
`;

const UploadedImageContainer = styled.div`
    position: relative;
    width: 80px;
    height: 80px;
`;

const UploadedImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`;

const DeleteButton = styled.button`
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    img {
        width: 12px;
        height: 12px;
    }

    &:hover {
        background: #f5f5f5;
    }
`;

const ButtonWrapper = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    z-index: 100;
`;
